import { Menu, Bell, Cake, UserPlus, AlertCircle, LogOut, Settings, X, UserCircle, CreditCard, Lock, RefreshCw, Sparkles } from "lucide-react";
import { useSidebarState } from "@/hooks/useSidebarState";
import { useAuth } from "@/hooks/useAuth";
import { useChurch } from "@/hooks/useChurch";
import { useSubscription } from "@/hooks/useSubscription";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { startOfWeek, endOfWeek, startOfMonth, format, differenceInYears, parseISO } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

type NotificationPrefs = {
  new_member: boolean;
  birthday: boolean;
  team_updates: boolean;
  data_alerts: boolean;
  finance: boolean;
};

const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = {
  new_member: true,
  birthday: true,
  team_updates: true,
  data_alerts: true,
  finance: true,
};

export default function AppHeader() {
  const { setMobileOpen } = useSidebarState();
  const navigate = useNavigate();
  const { profile, isOwner } = useChurch();
  const { user, role, signOut } = useAuth();
  const { subscription, isActive, loading: subscriptionLoading } = useSubscription();
  const churchId = profile?.id;

  const [notifOpen, setNotifOpen] = useState(false);
  const [notifFilter, setNotifFilter] = useState<"unread" | "all">("unread");
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  const notificationPrefsKey = user?.id ? `notification_prefs_${user.id}` : null;
  const [notificationPrefs, setNotificationPrefs] = useState<NotificationPrefs>(DEFAULT_NOTIFICATION_PREFS);

  useEffect(() => {
    if (!notificationPrefsKey || typeof window === "undefined") return;

    const loadPrefs = () => {
      try {
        const raw = window.localStorage.getItem(notificationPrefsKey);
        if (!raw) {
          setNotificationPrefs(DEFAULT_NOTIFICATION_PREFS);
          return;
        }

        const parsed = JSON.parse(raw) as Partial<NotificationPrefs>;
        setNotificationPrefs({ ...DEFAULT_NOTIFICATION_PREFS, ...parsed });
      } catch {
        setNotificationPrefs(DEFAULT_NOTIFICATION_PREFS);
      }
    };

    loadPrefs();

    const onStorage = (e: StorageEvent) => {
      if (e.key === notificationPrefsKey) loadPrefs();
    };

    const onPrefsUpdated = () => loadPrefs();

    window.addEventListener("storage", onStorage);
    window.addEventListener("notification-prefs-updated", onPrefsUpdated);

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("notification-prefs-updated", onPrefsUpdated);
    };
  }, [notificationPrefsKey]);

  const { data: userProfile } = useQuery({
    queryKey: ["user_profile_header", user?.id],
    queryFn: async () => {
      const { data, error } = await (supabase
        .from("profiles")
        .select("display_name, avatar_url")
        .eq("user_id", user!.id)
        .maybeSingle() as any);
      if (error) throw error;
      return data as { display_name: string | null; avatar_url: string | null } | null;
    },
    enabled: !!user?.id,
  });

  const { data: members = [] } = useQuery({
    queryKey: ["header_notifications", churchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("members")
        .select("id, full_name, phone, email, date_of_birth, gender, address, city, state, zip_code, date_joined, department, education_status, employment_status, status, family_household, created_at, updated_at")
        .eq("church_id", churchId!);
      if (error) throw error;
      return data || [];
    },
    enabled: !!churchId,
  });

  const { data: financeRecords = [] } = useQuery({
    queryKey: ["header_finance_notifications", churchId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("finance_records")
        .select("id, amount, category, record_type, created_at")
        .eq("church_id", churchId!)
        .order("created_at", { ascending: false })
        .limit(10);
      if (error) throw error;
      return data || [];
    },
    enabled: !!churchId,
  });

  const now = new Date();
  const weekStart = startOfWeek(now, { weekStartsOn: 0 });
  const weekEnd = endOfWeek(now, { weekStartsOn: 0 });
  const memberSnapshotKey = churchId ? `header_member_snapshot_${churchId}` : null;

  useEffect(() => {
    if (!memberSnapshotKey || typeof window === "undefined" || members.length === 0) return;

    const snapshot = Object.fromEntries(
      members.map((m) => [
        m.id,
        {
          updated_at: m.updated_at,
          phone: m.phone ?? "",
          email: m.email ?? "",
          date_of_birth: m.date_of_birth ?? "",
          address: m.address ?? "",
          city: m.city ?? "",
          status: m.status ?? "",
          family_household: m.family_household ?? "",
        },
      ])
    );

    window.localStorage.setItem(memberSnapshotKey, JSON.stringify(snapshot));
  }, [memberSnapshotKey, members]);

  const notifications = useMemo(() => {
    const items: { id: string; icon: "birthday" | "new_member" | "alert" | "update" | "finance"; title: string; detail: string; link: string }[] = [];
    members.filter((m) => m.date_of_birth).forEach((m) => {
      const dob = parseISO(m.date_of_birth!);
      const bday = new Date(now.getFullYear(), dob.getMonth(), dob.getDate());
      if (bday >= weekStart && bday <= weekEnd) {
        const age = differenceInYears(bday, dob);
        items.push({ id: `bday-${m.id}`, icon: "birthday", title: `${m.full_name}'s Birthday`, detail: `${format(bday, "MMM d")} · Turning ${age}`, link: "/people?tab=members" });
      }
    });
    const monthStart = startOfMonth(now).toISOString();
    members.filter((m) => m.created_at >= monthStart).sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).slice(0, 3).forEach((m) => {
      items.push({ id: `new-${m.id}`, icon: "new_member", title: "New Member Added", detail: `${m.full_name} · ${format(new Date(m.created_at), "MMM d")}`, link: "/people?tab=members" });
    });

    const trackedFields = [
      { key: "phone", label: "Phone" },
      { key: "email", label: "Email" },
      { key: "date_of_birth", label: "Birthday" },
      { key: "address", label: "Address" },
      { key: "city", label: "City" },
      { key: "status", label: "Status" },
      { key: "family_household", label: "Family" },
    ] as const;

    let previousSnapshot: Record<string, Record<string, string>> = {};
    if (memberSnapshotKey && typeof window !== "undefined") {
      try {
        previousSnapshot = JSON.parse(window.localStorage.getItem(memberSnapshotKey) || "{}");
      } catch {
        previousSnapshot = {};
      }
    }

    const sevenDaysAgo = new Date(now);
    sevenDaysAgo.setDate(now.getDate() - 7);
    members
      .filter((m) => {
        const updatedAt = new Date(m.updated_at);
        const createdAt = new Date(m.created_at);
        // Skip brand-new rows and only show meaningful edits from the last 7 days.
        return updatedAt >= sevenDaysAgo && updatedAt > new Date(createdAt.getTime() + 60 * 60 * 1000);
      })
      .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
      .slice(0, 3)
      .forEach((m) => {
        const prev = previousSnapshot[m.id];
        const changedLabels = prev
          ? trackedFields
              .filter((f) => {
                const previousValue = (prev[f.key] ?? "").toString().trim();
                const currentValue = ((m as Record<string, string | null>)[f.key] ?? "").toString().trim();
                return previousValue !== currentValue;
              })
              .map((f) => f.label)
          : [];

        const detailText =
          changedLabels.length === 0
            ? `${m.full_name} · profile updated by your team`
            : changedLabels.length <= 2
              ? `${m.full_name} · ${changedLabels.join(" and ")} updated by your team`
              : `${m.full_name} · ${changedLabels.slice(0, 2).join(", ")} +${changedLabels.length - 2} more updated by your team`;

        items.push({
          id: `upd-${m.id}-${m.updated_at}`,
          icon: "update",
          title: "Member Record Updated",
          detail: detailText,
          link: "/people?tab=members",
        });
      });
    const dataQualityFields = [
      { key: "phone", title: "Missing Phone Numbers", detailLabel: "phone" },
      { key: "email", title: "Missing Emails", detailLabel: "email" },
      { key: "date_of_birth", title: "Missing Birthdays", detailLabel: "birthday" },
      { key: "gender", title: "Missing Gender", detailLabel: "gender" },
      { key: "address", title: "Missing Addresses", detailLabel: "address" },
      { key: "city", title: "Missing Cities", detailLabel: "city" },
      { key: "state", title: "Missing States", detailLabel: "state" },
      { key: "zip_code", title: "Missing Zip Codes", detailLabel: "zip code" },
      { key: "date_joined", title: "Missing Date Joined", detailLabel: "date joined" },
      { key: "department", title: "Missing Ministries", detailLabel: "ministry" },
      { key: "education_status", title: "Missing Education", detailLabel: "education" },
      { key: "employment_status", title: "Missing Employment", detailLabel: "employment" },
    ] as const;

    dataQualityFields.forEach((field) => {
      const missingCount = members.filter((m) => !(m as Record<string, string | null>)[field.key]).length;
      if (missingCount > 0) {
        items.push({
          id: `alert-${field.key}`,
          icon: "alert",
          title: field.title,
          detail: `${missingCount} member${missingCount > 1 ? "s" : ""} without ${field.detailLabel}`,
          link: "/missing-info",
        });
      }
    });

    financeRecords
      .filter((r) => new Date(r.created_at) >= sevenDaysAgo)
      .slice(0, 3)
      .forEach((r) => {
        const amountText = Number(r.amount).toLocaleString(undefined, { maximumFractionDigits: 2 });
        const typeText = String(r.record_type || "record").toLowerCase();
        items.push({
          id: `fin-${r.id}`,
          icon: "finance",
          title: "Finance Record Added",
          detail: `${r.category} · $${amountText} ${typeText}`,
          link: "/finance",
        });
      });

    return items.filter((item) => {
      if (item.icon === "new_member") return notificationPrefs.new_member;
      if (item.icon === "birthday") return notificationPrefs.birthday;
      if (item.icon === "update") return notificationPrefs.team_updates;
      if (item.icon === "alert") return notificationPrefs.data_alerts;
      if (item.icon === "finance") return notificationPrefs.finance;
      return true;
    });
  }, [financeRecords, memberSnapshotKey, members, notificationPrefs, weekStart, weekEnd]);

  const iconMap = {
    birthday: <Cake className="w-4 h-4 text-accent" />,
    new_member: <UserPlus className="w-4 h-4 text-success" />,
    update: <RefreshCw className="w-4 h-4 text-primary" />,
    finance: <CreditCard className="w-4 h-4 text-success" />,
    alert: <AlertCircle className="w-4 h-4 text-destructive" />,
  };
  const unreadNotifications = notifications.filter((n) => !dismissedIds.has(n.id));
  const filteredNotifications = notifFilter === "unread" ? unreadNotifications : notifications;
  const handleNotificationClick = (link: string) => { setNotifOpen(false); navigate(link); };
  const handleDismiss = (e: React.MouseEvent, id: string) => { e.stopPropagation(); setDismissedIds((prev) => new Set(prev).add(id)); };

  const displayName = userProfile?.display_name || user?.email?.split("@")[0] || "User";
  const initials = displayName.split(" ").map((n: string) => n[0]?.toUpperCase()).join("").slice(0, 2);
  const avatarUrl = userProfile?.avatar_url || null;
  const subscriptionPeriodEnd = subscription?.current_period_end
    ? new Date(subscription.current_period_end)
    : null;
  const isScheduledToEnd =
    subscription?.status === "canceled" &&
    !!subscriptionPeriodEnd &&
    subscriptionPeriodEnd.getTime() > Date.now();
  const hasSubscriptionEnded =
    !!subscription &&
    !isActive &&
    (!subscriptionPeriodEnd || subscriptionPeriodEnd.getTime() <= Date.now());
  const showSubscriptionStatusBadge = isScheduledToEnd || hasSubscriptionEnded;
  const subscriptionStatusBadgeLabel = isScheduledToEnd ? "Subscription ending" : "Subscription ended";
  const showViewPlanBadge = !subscriptionLoading && !subscription;

  return (
    <header className="sticky top-0 z-30 flex min-h-[3.5rem] items-center justify-between border-b border-slate-200/70 bg-[#fbfdff]/92 px-3 sm:min-h-16 sm:px-6 lg:px-8 backdrop-blur">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <button onClick={() => setMobileOpen(true)} className="flex h-11 w-11 items-center justify-center rounded-lg transition-colors active:bg-secondary md:hidden">
          <Menu className="w-5 h-5 text-foreground" />
        </button>
      </div>
      <div className="flex items-center gap-3 sm:gap-4">
        {showViewPlanBadge && isOwner && (
          <button
            type="button"
            onClick={() => navigate("/subscription")}
            className="hidden sm:inline-flex items-center rounded-full bg-[#dbeafe] px-3.5 py-1 text-xs font-semibold text-[#1e3a8a] tracking-wide transition-colors hover:bg-[#c7dbf8]"
            title="View subscription plans"
          >
            View Plan
          </button>
        )}
        {showSubscriptionStatusBadge && isOwner && (
          <button
            type="button"
            onClick={() => navigate("/billing")}
            className="hidden sm:inline-flex items-center rounded-full bg-[#f7d7d7] px-3.5 py-1 text-xs font-semibold text-[#8a1e1e] tracking-wide transition-colors hover:bg-[#efc5c5]"
            title="Go to Billing"
          >
            {subscriptionStatusBadgeLabel}
          </button>
        )}

        <Popover open={notifOpen} onOpenChange={setNotifOpen}>
          <PopoverTrigger asChild>
            <button className="relative flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-secondary" title="Notifications">
              <Bell className="w-5 h-5 text-muted-foreground" />
              {unreadNotifications.length > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-accent rounded-full" />
              )}
            </button>
          </PopoverTrigger>
          <PopoverContent align="center" className="w-[calc(100vw-1rem)] max-w-80 p-0">
            <div className="p-3 border-b border-border">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-foreground">Notifications</h4>
                <Badge variant="secondary" className="text-xs">
                  {notifFilter === "unread" ? unreadNotifications.length : notifications.length}
                </Badge>
              </div>
              <div className="mt-2 inline-flex items-center rounded-full border border-border bg-background p-0.5">
                <button
                  type="button"
                  onClick={() => setNotifFilter("unread")}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                    notifFilter === "unread"
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  Unread
                </button>
                <button
                  type="button"
                  onClick={() => setNotifFilter("all")}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                    notifFilter === "all"
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  All
                </button>
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {filteredNotifications.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  {notifFilter === "unread" ? "No unread notifications" : "No notifications"}
                </p>
              ) : (
                filteredNotifications.map((n, i) => {
                  const isUnread = !dismissedIds.has(n.id);

                  return (
                  <div key={n.id}>
                    <div className={`group flex items-start gap-3 px-3 py-2.5 transition-colors hover:bg-secondary/50 ${isUnread ? "" : "opacity-70"}`}>
                      <button className="flex-1 text-left flex items-start gap-3 cursor-pointer" onClick={() => handleNotificationClick(n.link)}>
                        <div className="mt-0.5 flex-shrink-0">{iconMap[n.icon]}</div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-foreground">{n.title}</p>
                          <p className="text-xs text-muted-foreground">{n.detail}</p>
                        </div>
                      </button>
                      {isUnread && (
                        <button onClick={(e) => handleDismiss(e, n.id)} className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded transition-colors opacity-0 group-hover:opacity-100 hover:bg-muted" title="Mark as read">
                          <X className="w-3.5 h-3.5 text-muted-foreground" />
                        </button>
                      )}
                    </div>
                    {i < filteredNotifications.length - 1 && <Separator />}
                  </div>
                  );
                })
              )}
            </div>
          </PopoverContent>
        </Popover>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-2.5 rounded-full py-1 px-1 pr-2 transition-colors hover:bg-secondary focus:outline-none focus-visible:outline-none focus-visible:ring-0"
              title={displayName}
            >
              <Avatar className="w-9 h-9 border-2 border-[#1e3a8a]/20">
                <AvatarImage src={avatarUrl ?? undefined} alt={displayName} className="object-cover" />
                <AvatarFallback className="bg-[#1e3a8a] text-white text-xs font-semibold">{initials}</AvatarFallback>
              </Avatar>
              <div className="hidden sm:block text-left">
                <p className="text-sm font-semibold text-foreground leading-tight">{displayName}</p>
                <p className="text-xs text-muted-foreground capitalize leading-tight">{role || "Member"}</p>
              </div>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium text-foreground">{displayName}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
                {role && <Badge variant="secondary" className="w-fit text-xs capitalize mt-1">{role}</Badge>}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate("/profile")} className="cursor-pointer">
              <UserCircle className="w-4 h-4 mr-2" /> Profile
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/account")} className="cursor-pointer">
              <Lock className="w-4 h-4 mr-2" /> Account
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/settings")} className="cursor-pointer">
              <Settings className="w-4 h-4 mr-2" /> Settings
            </DropdownMenuItem>
            {isOwner && (
              <>
                <DropdownMenuItem onClick={() => navigate("/billing")} className="cursor-pointer">
                  <CreditCard className="w-4 h-4 mr-2" /> Billing
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/subscription")} className="cursor-pointer">
                  <Sparkles className="w-4 h-4 mr-2" /> Subscription
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => signOut()} className="cursor-pointer text-destructive focus:text-destructive">
              <LogOut className="w-4 h-4 mr-2" /> Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
